import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import usePageTitle from "../hooks/usePageTitle";
import "./BotsPage.css";
import "./BotsSidebar.css";
import { BotCard, SidebarBotItem } from "../components/BotComponents";
import CategoriesModal from "../components/CategoriesModal";
import VoteModal from "../components/VoteModal";
import SuccessModal from "../components/SuccessModal";
import authService from "../services/discordAuth";
import { useTranslation, Trans } from "react-i18next";

function BotsPage() {
  const { t } = useTranslation();

  const SORT_OPTIONS = useMemo(
    () => [
      { value: "votes", label: t("bots.sortOptions.votes") },
      { value: "rating", label: t("bots.sortOptions.rating") },
      { value: "servers", label: t("bots.sortOptions.servers") },
      { value: "newest", label: t("bots.sortOptions.newest") },
    ],
    [t],
  );

  usePageTitle(t("bots.pageTitle"), t("bots.pageDescription"));

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategories, setSelectedCategories] = useState(["Todos"]);
  const [sortBy, setSortBy] = useState("votes");
  const [currentPage, setCurrentPage] = useState(1);
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);
  const [showVoteModal, setShowVoteModal] = useState(false);
  const [selectedBot, setSelectedBot] = useState(null);

  // Estados para as seções e suas paginações
  const [premiumBots, setPremiumBots] = useState([]);
  const [premiumPage, setPremiumPage] = useState(1);
  const [premiumPagination, setPremiumPagination] = useState({ totalPages: 1 });

  const [topVotedBots, setTopVotedBots] = useState([]);
  const [votedPage, setVotedPage] = useState(1);
  const [votedPagination, setVotedPagination] = useState({ totalPages: 1 });

  const [newBots, setNewBots] = useState([]);
  const [newPage, setNewPage] = useState(1);
  const [newPagination, setNewPagination] = useState({ totalPages: 1 });

  // Estado para busca unificada
  const [searchResults, setSearchResults] = useState([]);

  const [categories, setCategories] = useState(["Todos"]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalBots, setTotalBots] = useState(0);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [showCooldownModal, setShowCooldownModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({ title: "", message: "" });

  const IS_SEARCHING = searchTerm.trim() !== "" || !selectedCategories.includes("Todos");
  const BOTS_PER_PAGE = 12;
  const BOTS_PER_SECTION = 4;

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

  // Buscar categorias
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/bots/categories`);
        const data = await response.json();
        const categoryNames = data.map((cat) => cat.name);
        setCategories(["Todos", ...categoryNames.slice(0, 10), "+ Categorias"]);
      } catch (err) {
        console.error("Erro ao buscar categorias:", err);
      }
    };
    fetchCategories();
  }, [BACKEND_URL]);

  // Buscar bots (Seções ou Busca)
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (IS_SEARCHING) {
          // Busca unificada
          const category = selectedCategories.includes("Todos") ? "" : selectedCategories.join(",");
          const res = await fetch(`${BACKEND_URL}/api/bots?search=${searchTerm}&category=${category}&sortBy=${sortBy}&page=${currentPage}&limit=${BOTS_PER_PAGE}&status=approved`);
          const data = await res.json();
          setSearchResults(data.bots || []);
          setTotalBots(data.pagination?.total || 0);
        } else {
          // Busca em seções (Home das Bots)
          const [premiumRes, votedRes, newRes] = await Promise.all([
            fetch(`${BACKEND_URL}/api/bots?tier=premium_business&limit=${BOTS_PER_SECTION}&page=${premiumPage}&status=approved`),
            fetch(`${BACKEND_URL}/api/bots?sortBy=votes&limit=${BOTS_PER_SECTION}&page=${votedPage}&tier=free&status=approved`),
            fetch(`${BACKEND_URL}/api/bots?sortBy=newest&limit=${BOTS_PER_SECTION}&page=${newPage}&tier=free&status=approved`),
          ]);

          const premiumData = await premiumRes.json();
          const votedData = await votedRes.json();
          const newData = await newRes.json();

          setPremiumBots(premiumData.bots || []);
          setPremiumPagination(premiumData.pagination || { totalPages: 1 });

          setTopVotedBots(votedData.bots || []);
          setVotedPagination(votedData.pagination || { totalPages: 1 });

          setNewBots(newData.bots || []);
          setNewPagination(newData.pagination || { totalPages: 1 });

          setTotalBots(votedData.pagination?.total || 0);
        }
      } catch (err) {
        console.error("Erro ao buscar bots:", err);
        setError(t("bots.errorTitle"));
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [searchTerm, selectedCategories, sortBy, currentPage, premiumPage, votedPage, newPage, BACKEND_URL, IS_SEARCHING, t]);

  // Toggle categoria
  const toggleCategory = (category) => {
    // Se clicar em "+ Categorias", abre modal (futuro)
    if (category === "+ Categorias") {
      setShowCategoriesModal(true);
      return;
    }

    if (category === "Todos") {
      setSelectedCategories(["Todos"]);
    } else {
      setSelectedCategories((prev) => {
        // Remove "Todos" se existir
        const withoutAll = prev.filter((c) => c !== "Todos");

        // Toggle da categoria
        if (withoutAll.includes(category)) {
          const newCategories = withoutAll.filter((c) => c !== category);
          return newCategories.length === 0 ? ["Todos"] : newCategories;
        } else {
          return [...withoutAll, category];
        }
      });
    }
    // Reset página ao mudar filtro
    setCurrentPage(1);
  };

  // Paginação
  const totalPages = Math.ceil(totalBots / BOTS_PER_PAGE);
  // Sidebar data - usar os mais votados e recentes das seções ou resultados
  const recentlyVoted = (IS_SEARCHING ? searchResults : topVotedBots).slice(0, 5);
  const recentlyAdded = (IS_SEARCHING ? searchResults : newBots).slice(0, 5);

  // Mudar página
  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // useEffect para atualizar o contador de cooldown em tempo real
  useEffect(() => {
    let interval;
    if (showCooldownModal && cooldownSeconds > 0) {
      interval = setInterval(() => {
        setCooldownSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setShowCooldownModal(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [showCooldownModal, cooldownSeconds]);

  // Função para formatar tempo de cooldown
  const formatCooldownTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}h ${minutes}m ${secs}s`;
  };

  // Handle vote - verificar cooldown primeiro
  const handleVote = async (bot) => {
    try {
      const token = authService.getAccessToken();
      if (!token) {
        setModalConfig({
          title: t("bots.loginRequiredTitle"),
          message: t("bots.loginRequiredMessage"),
        });
        setShowSuccessModal(true);
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/bots/vote/cooldown`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.ok && data.canVote) {
        // Pode votar, abrir modal
        setSelectedBot(bot);
        setShowVoteModal(true);
      } else if (data.cooldown) {
        // Em cooldown, mostrar modal com contador em tempo real
        setCooldownSeconds(data.remainingSeconds);
        setShowCooldownModal(true);
      }
    } catch (err) {
      console.error("Erro ao verificar cooldown:", err);
    }
  };

  return (
    <div className="bots-page">
      {/* Header */}
      <div className="bots-header">
        <div className="container">
          <Link to="/" className="back-button">
            <span>←</span> {t("bots.backButton")}
          </Link>
          <h1 className="page-title">
            <Trans i18nKey="bots.heroTitle" defaults="Explore <span className='gradient-text'>Todos os Bots</span>" components={{ span: <span className="gradient-text" /> }} />
          </h1>
          <p className="page-subtitle">{t("bots.heroSubtitle")}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="container">
          <div className="filters-grid">
            {/* Search */}
            <div className="filter-group search-group">
              <div className="search-box">
                <span className="search-icon">🔍</span>
                <input type="text" placeholder={t("bots.searchPlaceholder")} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-input" />
              </div>
            </div>

            {/* Categories */}
            <div className="filter-group">
              <label className="filter-label">{t("bots.filterCategory")}</label>
              <div className="category-pills">
                {categories.map((category) => (
                  <button
                    key={category}
                    className={`category-pill ${category === "+ Categorias" ? "more-categories" : selectedCategories.includes(category) ? "active" : ""}`}
                    onClick={() => toggleCategory(category)}
                  >
                    {category === "Todos" ? t("bots.allCategories") : category === "+ Categorias" ? t("bots.moreCategories") : category}
                  </button>
                ))}
              </div>
            </div>

            {/* Sort */}
            <div className="filter-row">
              <div className="filter-group">
                <label className="filter-label">{t("bots.filterSort")}</label>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sort-select">
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Results Count */}
          <div className="results-info">
            <p>{totalBots === 1 ? t("bots.resultsFound", { count: totalBots }) : t("bots.resultsFoundPlural", { count: totalBots })}</p>
          </div>
        </div>
      </div>

      {/* Bots Grid com Sidebar */}
      <div className="bots-list-section">
        <div className="container">
          {loading ? (
            <div className="no-results">
              <div className="no-results-icon">🔄</div>
              <h3>{t("bots.loadingTitle")}</h3>
              <p>{t("bots.loadingSubtitle")}</p>
            </div>
          ) : error ? (
            <div className="no-results">
              <div className="no-results-icon">❌</div>
              <h3>{t("bots.errorTitle")}</h3>
              <p>{error}</p>
            </div>
          ) : (IS_SEARCHING ? searchResults.length === 0 : premiumBots.length === 0 && topVotedBots.length === 0 && newBots.length === 0) ? (
            <div className="no-results">
              <div className="no-results-icon">😕</div>
              <h3>{t("bots.noBotsFound")}</h3>
              <p>{t("bots.noBotsSubtitle")}</p>
            </div>
          ) : (
            <div className="bots-content-wrapper">
              {/* Main Content */}
              <div className="bots-main-content">
                {IS_SEARCHING ? (
                  <>
                    <div className="section-divider">
                      <h2 className="section-divider-title">{t("bots.searchResultTitle")}</h2>
                      <p className="section-divider-subtitle">{t("bots.searchResultSubtitle")}</p>
                    </div>
                    <div className="bots-grid">
                      {searchResults.map((bot) => (
                        <BotCard key={bot.id} bot={bot} onVote={handleVote} />
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Bots Premium/Business */}
                    {premiumBots.length > 0 && (
                      <div className="bots-section-wrapper" style={{ marginBottom: "80px" }}>
                        <div className="section-divider">
                          <h2 className="section-divider-title">{t("bots.premiumSectionTitle")}</h2>
                          <p className="section-divider-subtitle">{t("bots.premiumSectionSubtitle")}</p>
                        </div>
                        <div className="bots-grid">
                          {premiumBots.map((bot) => (
                            <BotCard key={`premium-${bot.id}`} bot={bot} onVote={handleVote} />
                          ))}
                        </div>
                        {premiumPagination.totalPages > 1 && (
                          <div className="section-pagination">
                            <button className="pagination-btn mini" onClick={() => setPremiumPage((p) => Math.max(1, p - 1))} disabled={premiumPage === 1}>
                              ←
                            </button>
                            <span className="pagination-info">
                              {t("bots.paginationInfo", {
                                current: premiumPage,
                                total: premiumPagination.totalPages,
                              })}
                            </span>
                            <button
                              className="pagination-btn mini"
                              onClick={() => setPremiumPage((p) => Math.min(premiumPagination.totalPages, p + 1))}
                              disabled={premiumPage === premiumPagination.totalPages}
                            >
                              →
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Mais Votados */}
                    {topVotedBots.length > 0 && (
                      <div className="bots-section-wrapper" style={{ marginBottom: "80px" }}>
                        <div className="section-divider">
                          <h2 className="section-divider-title">{t("bots.topVotedTitle")}</h2>
                          <p className="section-divider-subtitle">{t("bots.topVotedSubtitle")}</p>
                        </div>
                        <div className="bots-grid">
                          {topVotedBots.map((bot) => (
                            <BotCard key={`voted-${bot.id}`} bot={bot} onVote={handleVote} />
                          ))}
                        </div>
                        {votedPagination.totalPages > 1 && (
                          <div className="section-pagination">
                            <button className="pagination-btn mini" onClick={() => setVotedPage((p) => Math.max(1, p - 1))} disabled={votedPage === 1}>
                              ←
                            </button>
                            <span className="pagination-info">
                              {t("bots.paginationInfo", {
                                current: votedPage,
                                total: votedPagination.totalPages,
                              })}
                            </span>
                            <button
                              className="pagination-btn mini"
                              onClick={() => setVotedPage((p) => Math.min(votedPagination.totalPages, p + 1))}
                              disabled={votedPage === votedPagination.totalPages}
                            >
                              →
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Novos Bots */}
                    {newBots.length > 0 && (
                      <div className="bots-section-wrapper">
                        <div className="section-divider">
                          <h2 className="section-divider-title">{t("bots.newBotsTitle")}</h2>
                          <p className="section-divider-subtitle">{t("bots.newBotsSubtitle")}</p>
                        </div>
                        <div className="bots-grid">
                          {newBots.map((bot) => (
                            <BotCard key={`new-${bot.id}`} bot={bot} onVote={handleVote} />
                          ))}
                        </div>
                        {newPagination.totalPages > 1 && (
                          <div className="section-pagination">
                            <button className="pagination-btn mini" onClick={() => setNewPage((p) => Math.max(1, p - 1))} disabled={newPage === 1}>
                              ←
                            </button>
                            <span className="pagination-info">
                              {t("bots.paginationInfo", {
                                current: newPage,
                                total: newPagination.totalPages,
                              })}
                            </span>
                            <button
                              className="pagination-btn mini"
                              onClick={() => setNewPage((p) => Math.min(newPagination.totalPages, p + 1))}
                              disabled={newPage === newPagination.totalPages}
                            >
                              →
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* Paginação */}
                {totalPages > 1 && (
                  <div className="pagination">
                    <button className="pagination-btn" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
                      ← {t("bots.paginationPrev")}
                    </button>

                    <div className="pagination-numbers">
                      {[...Array(totalPages)].map((_, index) => (
                        <button key={index + 1} className={`pagination-number ${currentPage === index + 1 ? "active" : ""}`} onClick={() => goToPage(index + 1)}>
                          {index + 1}
                        </button>
                      ))}
                    </div>

                    <button className="pagination-btn" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>
                      {t("bots.paginationNext")} →
                    </button>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <aside className="bots-sidebar">
                {/* Votados Recentemente */}
                <div className="sidebar-card">
                  <h3 className="sidebar-card-title">
                    <span>🔥</span> {t("bots.sidebarRecentVotes")}
                  </h3>
                  {recentlyVoted.map((bot) => (
                    <SidebarBotItem key={`voted-${bot.id}`} bot={bot} />
                  ))}
                </div>

                {/* Recém Adicionados */}
                <div className="sidebar-card">
                  <h3 className="sidebar-card-title">
                    <span>✨</span> {t("bots.sidebarRecentAdded")}
                  </h3>
                  {recentlyAdded.map((bot) => (
                    <SidebarBotItem key={`new-${bot.id}`} bot={bot} />
                  ))}
                </div>
              </aside>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Categorias */}
      <CategoriesModal isOpen={showCategoriesModal} onClose={() => setShowCategoriesModal(false)} selectedCategories={selectedCategories} onToggleCategory={toggleCategory} />

      {/* Vote Modal */}
      <VoteModal isOpen={showVoteModal} onClose={() => setShowVoteModal(false)} bot={selectedBot} />

      {/* Cooldown Modal */}
      <SuccessModal
        isOpen={showCooldownModal}
        onClose={() => setShowCooldownModal(false)}
        title={t("bots.cooldownTitle")}
        message={t("bots.cooldownMessage", {
          time: formatCooldownTime(cooldownSeconds),
        })}
      />

      {/* Success/Error Modal */}
      <SuccessModal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)} title={modalConfig.title} message={modalConfig.message} />
    </div>
  );
}

export default BotsPage;
